/**
 * Offer lifecycle rules shared by the server transition
 * (`~/lib/server/offer-lifecycle`) and the coach's status control: which
 * status changes are allowed, which offers may be the main offer, which
 * offers a coach may edit, and which actions a coach sees for an offer in a
 * given state.
 *
 * Aliases: offer status, publish offer, retire offer, republish offer,
 * main offer rule, offer lifecycle actions, can edit offer, ProPlus offers
 */
import { MembershipPlan, OfferStatus, OfferType } from "~/lib/schemas/db-enums";

const ALLOWED_TRANSITIONS: Record<OfferStatus, readonly OfferStatus[]> = {
  [OfferStatus.Draft]: [OfferStatus.Published],
  [OfferStatus.Published]: [OfferStatus.Retired],
  [OfferStatus.Retired]: [OfferStatus.Published],
};

export function canTransitionOffer(
  from: OfferStatus,
  to: OfferStatus,
): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

/**
 * The profile's offer section renders the main offer with the standard offer
 * layout, so only a published standard offer qualifies. Mini Sprints and
 * summit specials are entry offers with their own pages.
 */
export function canBeMainOffer(offer: {
  status: OfferStatus;
  type: OfferType;
}): boolean {
  return (
    offer.status === OfferStatus.Published && offer.type === OfferType.Standard
  );
}

/**
 * Additional standard offers are a ProPlus feature. The primary offer, Mini
 * Sprints and summit specials are open to every coach. `offer` must be one of
 * the coach's own offers as stored, so its type cannot come from a request.
 */
export function canEditOffer(params: {
  offer: { id: string; type: OfferType };
  primaryOfferId: string | null;
  membershipPlan: MembershipPlan;
}): boolean {
  switch (params.offer.type) {
    case OfferType.MiniSprint:
    case OfferType.Summit:
      return true;
    case OfferType.Standard:
      return (
        params.offer.id === params.primaryOfferId ||
        params.membershipPlan === MembershipPlan.ProPlus
      );
    default: {
      const exhaustive: never = params.offer.type;
      throw new Error(`Unknown offer type: ${String(exhaustive)}`);
    }
  }
}

export type OfferStatusAction =
  | "publish"
  | "retire"
  | "republish"
  | "make_main";

/** The status each lifecycle action moves an offer to. */
export const OFFER_ACTION_TARGET_STATUS = {
  publish: OfferStatus.Published,
  retire: OfferStatus.Retired,
  republish: OfferStatus.Published,
} as const satisfies Record<
  Exclude<OfferStatusAction, "make_main">,
  OfferStatus
>;

/** The actions a coach can take on an offer, in display order. */
export function offerStatusActions(offer: {
  status: OfferStatus;
  type: OfferType;
  isMain: boolean;
}): OfferStatusAction[] {
  switch (offer.status) {
    case OfferStatus.Draft:
      return ["publish"];
    case OfferStatus.Published:
      return canBeMainOffer(offer) && !offer.isMain
        ? ["make_main", "retire"]
        : ["retire"];
    case OfferStatus.Retired:
      return ["republish"];
    default: {
      const exhaustive: never = offer.status;
      throw new Error(`Unknown offer status: ${String(exhaustive)}`);
    }
  }
}
